import { makeStyles } from '@mui/styles';
import type { Theme, SxProps } from '@mui/material/styles';
import { responsiveGridMaxHeight, getDataGridStyles } from '../../styles/MtComStyleObj';
// ── Shared responsive style objects (DRY) ────────────────────────────

export const RESPONSIVE_CELL_PADDING = { xs: '6px', sm: '8px', md: '10px' } as const;

export const RESPONSIVE_FOOTER_HEIGHT = { xs: 40, sm: 52 } as const;

export const RESPONSIVE_GRID_MAX_HEIGHT = responsiveGridMaxHeight(350);

export const kpiToggleButtonSx: SxProps = {
    minWidth: 'fit-content',
    height: { xs: 24, sm: 27 },
    borderRadius: '8px',
    paddingLeft: { xs: '8px', sm: '12px' },
    paddingRight: { xs: '8px', sm: '12px' },
    gap: { xs: '4px', sm: '8px' },
    fontSize: { xs: '12px', sm: '14px' },
    opacity: 1,
    textTransform: 'none',
    background: '#FFFFFF',
    border: '1px solid #404040',
    borderTop: '1px solid #404040',
    color: '#0A0A0A',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
        background: '#FFFFFF',
        border: '1px solid #404040',
        borderTop: '1px solid #404040',
    },
};

export const getBaseDataGridSx = (theme: Theme, overrides?: Record<string, any>) => ({
    width: '100%',
    ...getDataGridStyles(theme, {
        headerColor: '#DEDEDE',
        fillerColor: '#DEDEDE',
        maxHeight: 'calc(100vh - 400px)',
    }),
    ...overrides,
});

export const useCommonTrailStyles = makeStyles(() => ({
    // Common button styles
    buttonBase: {
        height: '40px',
        padding: '10px 16px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        '& .MuiButton-startIcon, & .MuiButton-endIcon': {
            display: 'inherit',
            alignItems: 'center',
        },
        '@media (max-width: 600px)': {
            height: '36px',
            padding: '8px 12px',
        },
    },
    downloadButton:{
        width: "133px",
        height: "40px",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "10px 16px",
        borderRadius: "4px",
        border: "1px solid #000",
        backgroundColor: "transparent",
        cursor: "pointer",
        opacity: 1,
        color: "#000000",
        fontSize: "12px",
        '& .MuiButton-startIcon, & .MuiButton-endIcon': {
            display: 'inherit',
            alignItems: 'center',
        },
        '@media (max-width: 600px)': {
            width: 'auto',
            minWidth: '100px',
            height: '36px',
            padding: '8px 12px',
            fontSize: '11px',
        },
    },
    // Common layout styles
    CenterAlignText: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    gridCellWithInput: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '4px 8px',
        '& .MuiFormControl-root': {
            margin: 0,
            width: '100%',
        },
        '& .MuiOutlinedInput-root': {
            fontSize: '14px',
            '& input': {
                padding: '8px 12px'
            }
        },
        '@media (max-width: 600px)': {
            padding: '2px 4px',
            '& .MuiOutlinedInput-root': {
                fontSize: '12px',
                '& input': {
                    padding: '6px 8px',
                },
            },
        },
    },
    gridProgressCell: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        padding: '8px',
        width: '100%',
        '@media (max-width: 600px)': {
            padding: '4px',
        },
    },
    gridActionCell: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '4px',
        '@media (max-width: 600px)': {
            padding: '2px',
        },
    },
    gridContainerScrollable: {
        width: '100%',
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'flex-start',
        overflow: 'auto',
        // '@media (max-width: 900px)': {
        //     height: '100%',
        // },
        // '@media (max-width: 600px)': {
        //     height: '100%',
        // },
    },
    flexRowWithGap: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flexWrap: 'wrap',
        '@media (max-width: 600px)': {
            gap: '4px',
        },
    },
    underlineButton: {
        background: "none",
        border: "none",
        cursor: "pointer",
        textDecoration: "underline"
    },
    // Filter specific styles
    saveFilterButton: {
        fontWeight: 400,
        fontStyle: 'normal',
        fontSize: 16,
        backgroundColor: '#ECECEC',
        textTransform: 'none',
        width: 'auto',
        color: "#000000",
        whiteSpace: 'nowrap',
        borderRadius: '4px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        '@media (max-width: 600px)': {
            fontSize: 14,
            padding: '6px 12px',
        },
    },
    filterContainer: {
        width: '100%',
        border: '2px solid #E5E7EB',
        boxSizing: 'border-box',
        padding: '12px 16px',
        '@media (max-width: 600px)': {
            padding: '8px 10px',
        },
    },
    filterTitle: {
        fontSize: 16,
        fontWeight: 400,
        lineHeight: 1,
        marginTop: '16px',
        '@media (max-width: 600px)': {
            fontSize: 14,
            marginTop: '12px',
        },
    },
    filterStack: {
        marginTop: '8px',
        marginBottom: '0px'
    },
    filterActionsBox: {
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: '4px',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '8px',
        '@media (max-width: 600px)': {
            flexDirection: 'column',
            alignItems: 'stretch',
        },
    },
    saveFilterInput: {
        width: '288px',
        '@media (max-width: 600px)': {
            width: '100%',
        },
    },
    filterButton: {
        cursor: 'pointer'
    },
    filterDivider: {
        marginTop: '8px'
    },
    filterFormControl: {
        width: 'calc(25% - 16px)',
        minWidth: '200px',
        display: 'flex',
        flexDirection: 'column',
        '@media (max-width: 1200px)': {
            width: 'calc(33.33% - 16px)'
        },
        '@media (max-width: 900px)': {
            width: 'calc(50% - 16px)'
        },
        '@media (max-width: 600px)': {
            width: '100%',
            minWidth: 'unset',
        }
    },
    filterTextField: {
        '& .MuiInputBase-root': {
            height: '40px'
        },
        '@media (max-width: 600px)': {
            '& .MuiInputBase-root': {
                height: '36px',
            },
        },
    },
    filterSaveButton: {
        height: '36px',
    },
    filterSaveInputControl: {
        display: 'flex',
        flexDirection: 'column'
    },
    filterViewSavedLink: {
        marginTop: '20px',
        cursor: 'pointer',
        '@media (max-width: 600px)': {
            marginTop: '12px',
        },
    },
    filterClearLink: {
        marginRight: '40px',
        marginTop: '20px',
        cursor: 'pointer',
        '@media (max-width: 600px)': {
            marginRight: '16px',
            marginTop: '12px',
        },
    },
    // Material details specific styles
    materialHeaderContainer: {
        width: '100%',
       
        '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 700,
            color: '#ffffff',
            fontSize: 16,
        },
        '@media (max-width: 600px)': {
            '& .MuiDataGrid-columnHeaderTitle': {
                fontSize: 14,
            },
        },
    },
    materialHeaderActionsBox: {
     
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '8px',
        '@media (max-width: 600px)': {
            marginTop: '12px',
            flexDirection: 'column',
            alignItems: 'stretch',
        },
    },
    materialHeaderLeftSection: {
        display: 'flex',
        flexDirection: 'column'
    },
    materialInfoText: {
        fontSize: '14px',
        color: 'gray',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flexWrap: 'wrap',
        '@media (max-width: 600px)': {
            fontSize: '12px',
            gap: '4px',
        },
    },
    materialNavContainer: {
        flexDirection: 'column',
        width: '100%'
    },
    materialBackButtonRow: {
        display: 'flex',
        justifyContent: 'flex-start'
    },
    materialCloseButtonRow: {
        display: 'flex',
        justifyContent: 'flex-end'
    },
    materialCloseIconButton: {
        color: '#9e9e9e'
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px',
        width: '100%',
        '@media (max-width: 600px)': {
            minHeight: '250px',
        },
    },
    // Customize columns panel styles
    customizeColumnsBackdrop: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9,
        backgroundColor: 'transparent',
    },
    customizeColumnsPaper: {
        position: 'absolute',
        top: '100%',
        zIndex: 10,
        width: '320px',
        backgroundColor: '#ffffff',
        border: '1px solid #e0e0e0',
        borderRadius: '4px',
        marginTop: '8px',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
        '@media (max-width: 600px)': {
            width: '100%',
            maxWidth: '320px',
            position: 'fixed',
            top: 'auto',
            bottom: 0,
            left: 0,
            right: 0,
            maxHeight: '70vh',
            borderRadius: '12px 12px 0 0',
            marginTop: 0,
        },
    },
    customizeColumnsContent: {
        padding: '16px',
        overflowY: 'auto',
        flex: '1 1 auto',
        height: '400px',
        '@media (max-width: 600px)': {
            padding: '12px',
            height: '50vh',
        },
    },
    columnItemRequired: {
        opacity: 0.6,
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        margin: 0,
        marginBottom: '8px',
        minHeight: '40px',
        padding: '4px 8px',
        border: '1px solid #e0e0e0',
        borderRadius: '4px',
        backgroundColor: 'rgb(249, 250, 251)',
        boxSizing: 'border-box',
        '& .MuiFormControlLabel-label': {
            flex: 1,
        },
    },
    columnItemOptional: {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        margin: 0,
        marginBottom: '8px',
        minHeight: '40px',
        padding: '4px 8px',
        border: '1px solid black',
        borderRadius: '4px',
        boxSizing: 'border-box',
        '& .MuiFormControlLabel-label': {
            flex: 1,
        },
    },
    columnCheckbox: {
        marginRight: '8px',
        '&.Mui-checked': {
            color: '#161616',
        },
    },
    columnLabelBox: {
        display: 'flex',
        width: '100%',
        justifyContent: 'space-between',
        alignItems: 'center',
        flex: 1,
    },
    requiredLabel: {
        fontSize: '12px',
        color: '#5F5F5F',
        marginLeft: 'auto',
        marginRight: '5px',
    },
    customizeColumnsFooter: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '8px',
        padding: '16px',
        paddingTop: 0,
        marginTop: '8px',
        '@media (max-width: 600px)': {
            padding: '12px',
            paddingTop: 0,
        },
    },
    customizeColumnSaveButton: {
        textTransform: 'none',
        fontWeight: 500,
        fontSize: '16px',
        lineHeight: '16px',
        letterSpacing: '0px',
        textAlign: 'center',
        backgroundColor: '#14803C',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        '@media (max-width: 600px)': {
            fontSize: '14px',
        },
    },
    customizeColumnResetButton: {
        textTransform: 'none',
        fontWeight: 500,
        fontSize: '16px',
        lineHeight: '16px',
        letterSpacing: '0px',
        textAlign: 'center',
        color: '#404040',
        borderRadius: '4px',
        borderColor: '#404040',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
         '&:hover': {
            backgroundColor: '#f5f5f5',
            borderColor: '#404040',
        },
        '@media (max-width: 600px)': {
            fontSize: '14px',
        },
    },
    selectedRowsBox: {
        backgroundColor: '#E8F5E9',
        border: '1px solid #4CAF50',
        borderRadius: '4px',
        padding: '12px 16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: '16px',
        flexWrap: 'wrap',
        gap: '8px',
        '@media (max-width: 600px)': {
            padding: '8px 12px',
            marginTop: '12px',
            flexDirection: 'column',
            alignItems: 'stretch',
        },
    }
}));
